import React, { useState, useEffect } from 'react';
import './MonitoringDashboard.css';

const ANALYTICS_API = 'https://fnddxpffx6n6dty6ftwteksjqq0buwua.lambda-url.us-east-1.on.aws';

const MonitoringDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [filters, setFilters] = useState({ type: 'all', source: 'all' });

  useEffect(() => { fetchData(); }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${ANALYTICS_API}?days=${days}&section=all`);
      setData(await res.json());
    } catch (e) { console.error('Analytics fetch error:', e); }
    setLoading(false);
  };

  const StatCard = ({ value, label, trend, trendType }) => (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {trend && <div className={`stat-trend ${trendType}`}>{trend}</div>}
    </div>
  );

  const BarChart = ({ data, xKey, bars, title }) => {
    if (!data?.length) return <div className="chart-empty">No data</div>;
    const max = Math.max(...data.flatMap(d => bars.map(b => d[b.key] || 0)));
    return (
      <div className="bar-chart">
        <h4>{title}</h4>
        {data.map((d, i) => (
          <div key={i} className="bar-row">
            <span className="bar-label">{d[xKey]}</span>
            <div className="bar-container">
              {bars.map(b => (
                <div key={b.key} className={`bar ${b.color}`} style={{ width: `${(d[b.key] || 0) / max * 100}%` }}>
                  {d[b.key] > 0 && <span>{d[b.key]}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="bar-legend">{bars.map(b => <span key={b.key} className={b.color}>● {b.label}</span>)}</div>
      </div>
    );
  };

  const renderOverview = () => {
    const o = data?.overview || {};
    return (
      <div className="overview-grid">
        <StatCard value={o.total_feedback || 0} label="Total Feedback" />
        <StatCard value={`${o.thumbs_down_rate || 0}%`} label="👎 Rate" trendType={o.thumbs_down_rate > 20 ? 'negative' : 'positive'} />
        <StatCard value={o.refusals || 0} label="Refusals (No Data)" />
        <StatCard value={o.episodes_written || 0} label="Learning Episodes" />
        
        <div className="chart-card wide">
          <BarChart data={data?.daily} xKey="date" title="Daily Feedback" bars={[
            { key: 'up', label: '👍', color: 'green' },
            { key: 'down', label: '👎', color: 'red' }
          ]} />
        </div>
        
        <div className="chart-card">
          <h4>Top Feedback Tags</h4>
          <div className="tag-list">
            {(data?.tags || []).slice(0, 6).map(t => (
              <div key={t.tag} className="tag-row">
                <span>{t.tag.replace('_', ' ')}</span><span className="tag-count">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="chart-card">
          <BarChart data={data?.response_types} xKey="type" title="Feedback by Response Type" bars={[
            { key: 'up', label: '👍', color: 'green' },
            { key: 'down', label: '👎', color: 'red' }
          ]} />
        </div>
      </div>
    );
  };

  const renderConfidence = () => (
    <div className="analytics-section">
      <h3>Confidence vs Feedback Outcome</h3>
      <p className="section-desc">Does higher confidence correlate with better feedback?</p>
      <BarChart data={data?.confidence} xKey="level" title="" bars={[
        { key: 'up', label: '👍', color: 'green' },
        { key: 'down', label: '👎', color: 'red' }
      ]} />
      
      <h3>Data Source Performance</h3>
      <p className="section-desc">Which data sources lead to better responses?</p>
      <BarChart data={data?.data_sources} xKey="source" title="" bars={[
        { key: 'up', label: '👍', color: 'green' },
        { key: 'down', label: '👎', color: 'red' }
      ]} />
    </div>
  );

  const renderFlagged = () => (
    <div className="flagged-section">
      <h3>Flagged for Review ({(data?.flagged || []).length})</h3>
      <p className="section-desc">Responses that received 👎 - review for patterns</p>
      <table className="flagged-table">
        <thead>
          <tr><th>Date</th><th>Type</th><th>Confidence</th><th>Tags</th><th>Comment</th></tr>
        </thead>
        <tbody>
          {(data?.flagged || []).map(f => (
            <tr key={f.id}>
              <td>{f.date?.split(' ')[0]}</td>
              <td><span className={`type-badge ${f.type?.toLowerCase()}`}>{f.type}</span></td>
              <td><span className={`conf-badge ${f.confidence?.toLowerCase()}`}>{f.confidence}</span></td>
              <td>{f.tags?.replace(/[{}]/g, '').split(',').map(t => <span key={t} className="tag-chip">{t}</span>)}</td>
              <td className="comment-cell">{f.comment || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderFilters = () => (
    <div className="dashboard-filters">
      <select value={days} onChange={e => setDays(Number(e.target.value))}>
        <option value={1}>Last 24 hours</option>
        <option value={7}>Last 7 days</option>
        <option value={30}>Last 30 days</option>
      </select>
      <select value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})}>
        <option value="all">All Response Types</option>
        <option value="FACT-REQUIRED">Fact-Required</option>
        <option value="EDUCATION">Education</option>
        <option value="STATS">Stats</option>
      </select>
      <button onClick={fetchData} disabled={loading}>{loading ? 'Loading...' : '↻ Refresh'}</button>
    </div>
  );

  return (
    <div className="monitoring-dashboard">
      <div className="dashboard-header">
        <h2>Agent Monitoring</h2>
        {renderFilters()}
      </div>
      
      <div className="dashboard-tabs">
        {['overview', 'confidence', 'flagged'].map(tab => (
          <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? '📊 Overview' : tab === 'confidence' ? '🎯 Confidence Analysis' : '🚩 Flagged'}
          </button>
        ))}
      </div>
      
      <div className="dashboard-content">
        {loading ? <div className="loading">Loading analytics...</div> : 
          activeTab === 'overview' ? renderOverview() :
          activeTab === 'confidence' ? renderConfidence() :
          renderFlagged()
        }
      </div>
    </div>
  );
};

export default MonitoringDashboard;
